import {
  buildThemeFromConfig,
  generateCSSVariables,
  getFontFamilyCSS,
  loadThemeFonts,
} from '@alnakib4trade/shared-theme';

type ThemeConfig = Record<string, unknown>;

// Must match the key read by the inline head script in nuxt.config.ts.
const THEME_CACHE_KEY = 'a4t_admin_theme_cache';
type ThemeMode = 'light' | 'dark' | 'auto' | 'user_choice';

const legacyKeys: Record<string, string[]> = {
  'theme.bg_tint': ['theme.colors.bg_tint'],
  'theme.typography.font_display': ['theme.typography.display_font'],
  'theme.typography.font_body': ['theme.typography.body_font'],
  'theme.typography.font_arabic': ['theme.typography.arabic_font'],
};

function value(config: ThemeConfig, key: string): unknown {
  if (config[key] !== undefined) return config[key];
  return legacyKeys[key]?.map((alias) => config[alias]).find((item) => item !== undefined);
}

function stringValue(config: ThemeConfig, key: string): string | undefined {
  const item = value(config, key);
  return typeof item === 'string' ? item : undefined;
}

function fontId(item?: string): string | undefined {
  return item?.trim().toLowerCase().replace(/[\s_]+/g, '-');
}

function resolveMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'light' || mode === 'dark') return mode;
  if (mode === 'user_choice') {
    const saved = localStorage.getItem('alnakib-theme-mode');
    if (saved === 'light' || saved === 'dark') return saved;
  }
  return matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export default defineNuxtPlugin(() => {
  const runtimeConfig = useRuntimeConfig();
  const media = matchMedia('(prefers-color-scheme: dark)');
  let currentConfig: ThemeConfig = {};

  async function applyConfig(config: ThemeConfig): Promise<void> {
    const configuredMode = (stringValue(config, 'theme.mode') || 'auto') as ThemeMode;
    const mode = resolveMode(configuredMode);
    const root = document.documentElement;
    const applied: Record<string, string> = {};
    const setVar = (key: string, item: string): void => {
      root.style.setProperty(key, item);
      applied[key] = item;
    };
    const fonts = {
      display: fontId(stringValue(config, 'theme.typography.font_display')),
      body: fontId(stringValue(config, 'theme.typography.font_body')),
      arabic: fontId(stringValue(config, 'theme.typography.font_arabic')),
      chinese: fontId(stringValue(config, 'theme.typography.font_chinese')),
      mono: fontId(stringValue(config, 'theme.typography.font_mono')),
    };

    const { theme } = buildThemeFromConfig({
      brand_primary: stringValue(config, 'theme.colors.brand_primary'),
      dark_brand_primary: stringValue(config, 'theme.dark.colors.brand_primary'),
      brand_secondary: stringValue(config, 'theme.colors.brand_secondary'),
      bg_tint: stringValue(config, 'theme.bg_tint') as 'warm' | 'cool' | 'neutral' | undefined,
      display_font: fonts.display,
      body_font: fonts.body,
      arabic_font: fonts.arabic,
      size_scale: stringValue(config, 'theme.typography.size_scale') as 'compact' | 'default' | 'large' | undefined,
      radius: stringValue(config, 'theme.radius') as 'sharp' | 'subtle' | 'rounded' | 'pill' | undefined,
      mode,
    });

    for (const [key, item] of Object.entries(generateCSSVariables(theme))) {
      setVar(key, item);
    }

    const lightOnly = mode === 'light';
    const directColors: Record<string, string | undefined> = {
      '--color-brand-primary': lightOnly ? stringValue(config, 'theme.colors.brand_primary') : undefined,
      '--color-brand-secondary': stringValue(config, 'theme.colors.brand_secondary'),
      '--color-brand-tertiary': stringValue(config, 'theme.colors.brand_tertiary'),
      '--color-bg-primary': lightOnly ? stringValue(config, 'theme.colors.bg_primary') : undefined,
      '--color-bg-secondary': lightOnly ? stringValue(config, 'theme.colors.bg_secondary') : undefined,
      '--color-bg-tertiary': lightOnly ? stringValue(config, 'theme.colors.bg_tertiary') : undefined,
      '--color-surface-primary': lightOnly ? stringValue(config, 'theme.colors.bg_primary') : undefined,
      '--color-surface-secondary': lightOnly ? stringValue(config, 'theme.colors.bg_secondary') : undefined,
      '--color-surface-tertiary': lightOnly ? stringValue(config, 'theme.colors.bg_tertiary') : undefined,
      '--color-surface-elevated': lightOnly ? stringValue(config, 'theme.colors.bg_elevated') : undefined,
      '--color-text-primary': lightOnly ? stringValue(config, 'theme.colors.text_primary') : undefined,
      '--color-text-secondary': lightOnly ? stringValue(config, 'theme.colors.text_secondary') : undefined,
      '--color-text-muted': lightOnly ? stringValue(config, 'theme.colors.text_muted') : undefined,
      '--color-border-primary': lightOnly ? stringValue(config, 'theme.colors.border_primary') : undefined,
      '--color-border-secondary': lightOnly ? stringValue(config, 'theme.colors.border_secondary') : undefined,
      '--color-border-focus': lightOnly ? stringValue(config, 'theme.colors.border_focus') : undefined,
      '--color-status-success': lightOnly ? stringValue(config, 'theme.colors.status_success') : undefined,
      '--color-status-warning': lightOnly ? stringValue(config, 'theme.colors.status_warning') : undefined,
      '--color-status-error': lightOnly ? stringValue(config, 'theme.colors.status_error') : undefined,
      '--color-status-info': lightOnly ? stringValue(config, 'theme.colors.status_info') : undefined,
      '--color-btn-primary-bg': lightOnly ? stringValue(config, 'theme.colors.button_primary_bg') : undefined,
      '--color-btn-primary-text': lightOnly ? stringValue(config, 'theme.colors.button_primary_text') : undefined,
      '--color-btn-primary-hover': lightOnly ? stringValue(config, 'theme.colors.button_primary_hover') : undefined,
      '--color-btn-secondary-bg': lightOnly ? stringValue(config, 'theme.colors.button_secondary_bg') : undefined,
      '--color-btn-secondary-text': lightOnly ? stringValue(config, 'theme.colors.button_secondary_text') : undefined,
      '--color-btn-secondary-hover': lightOnly ? stringValue(config, 'theme.colors.button_secondary_hover') : undefined,
    };

    for (const key of Object.keys(config).filter((item) => item.startsWith('theme.dark.colors.'))) {
      if (mode !== 'dark' || typeof config[key] !== 'string') continue;
      directColors[key.replace('theme.dark.colors.', '--color-').replace(/[._]/g, '-')] = config[key] as string;
    }

    for (const [key, item] of Object.entries(directColors)) {
      if (item) setVar(key, item);
    }

    const fontVariables = {
      '--font-display': fonts.display ? getFontFamilyCSS(fonts.display) : undefined,
      '--font-body': fonts.body ? getFontFamilyCSS(fonts.body) : undefined,
      '--font-arabic': fonts.arabic ? getFontFamilyCSS(fonts.arabic) : undefined,
      '--font-chinese': fonts.chinese ? getFontFamilyCSS(fonts.chinese) : undefined,
      '--font-mono': fonts.mono ? getFontFamilyCSS(fonts.mono) : undefined,
    };
    for (const [key, item] of Object.entries(fontVariables)) {
      if (item) setVar(key, item);
    }

    const componentVariables: Record<string, string> = {
      '--container-narrow': stringValue(config, 'theme.layout.container_narrow') || '640px',
      '--container-default': stringValue(config, 'theme.layout.container_default') || '1280px',
      '--container-wide': stringValue(config, 'theme.layout.container_wide') || '1536px',
      '--component-button-radius': `var(--radius-${stringValue(config, 'theme.components.button_radius') || 'md'})`,
      '--component-button-shadow': `var(--shadow-${stringValue(config, 'theme.components.button_shadow') || 'sm'})`,
      '--component-card-radius': `var(--radius-${stringValue(config, 'theme.components.card_radius') || 'lg'})`,
      '--component-card-shadow': `var(--shadow-${stringValue(config, 'theme.components.card_shadow') || 'sm'})`,
      '--component-input-radius': `var(--radius-${stringValue(config, 'theme.components.input_radius') || 'md'})`,
      '--component-modal-radius': `var(--radius-${stringValue(config, 'theme.components.modal_radius') || 'xl'})`,
      '--component-modal-shadow': `var(--shadow-${stringValue(config, 'theme.components.modal_shadow') || 'lg'})`,
      '--component-modal-overlay-opacity': String(value(config, 'theme.components.modal_overlay') ?? 0.5),
    };
    for (const [key, item] of Object.entries(componentVariables)) {
      setVar(key, item);
    }

    const customCss = stringValue(config, 'theme.custom_css');
    const existingCustomCss = document.getElementById('alnakib-custom-css');
    if (customCss) {
      const style = existingCustomCss || document.createElement('style');
      style.id = 'alnakib-custom-css';
      style.textContent = customCss;
      if (!existingCustomCss) document.head.appendChild(style);
    } else {
      existingCustomCss?.remove();
    }

    root.dataset.theme = mode;
    root.dataset.themeMode = configuredMode;
    root.style.colorScheme = mode;

    // Cache the resolved theme so the inline head script in nuxt.config.ts can
    // apply it before first paint on the next load (no light/dark or colour flash).
    try {
      const previous = JSON.parse(localStorage.getItem(THEME_CACHE_KEY) || '{}') as { vars?: Record<string, string> };
      // Drop variables the pre-paint script applied from the old cache that this config no longer sets.
      for (const key of Object.keys(previous.vars || {})) {
        if (!(key in applied)) root.style.removeProperty(key);
      }
      localStorage.setItem(THEME_CACHE_KEY, JSON.stringify({ mode, vars: applied }));
    } catch {
      // Storage may be unavailable (private mode); the flash is cosmetic only.
    }
    root.toggleAttribute(
      'data-reduce-motion',
      value(config, 'theme.animation.enabled') === false
        || value(config, 'theme.animation.reduce_motion') === true,
    );

    const bundled = new Set([
      'playfair-display',
      'dm-sans',
      'ibm-plex-sans-arabic',
      'noto-sans-sc',
      'jetbrains-mono',
    ]);
    await loadThemeFonts(Object.fromEntries(
      Object.entries(fonts).map(([key, item]) => [
        key,
        item && !bundled.has(item) ? item : undefined,
      ]),
    ));
  }

  async function load(): Promise<void> {
    try {
      const response = await $fetch<{ success: boolean; data?: ThemeConfig }>(
        `${runtimeConfig.public.apiBaseUrl}/config/theme`,
        { credentials: 'include' },
      );
      currentConfig = response.success && response.data ? response.data : {};
      await applyConfig(currentConfig);
    } catch {
      // Static CSS defaults remain usable if the configuration service is unavailable.
    }
  }

  const handlePreferenceChange = (): void => {
    const mode = (stringValue(currentConfig, 'theme.mode') || 'auto') as ThemeMode;
    if (mode === 'auto' || mode === 'user_choice') void applyConfig(currentConfig);
  };

  void load();
  media.addEventListener('change', handlePreferenceChange);
  window.addEventListener('storage', handlePreferenceChange);
  window.addEventListener('alnakib-theme-updated', load);
});
